
import React from "react";
import PromoSidebar from "../../../PromoSidebar/PromoSidebar";
import { motion } from "framer-motion";
import styles from "./london-after-dark.module.css";
import BlogNavigator from "../../../BlogNavigator/BlogNavigator.js";
import { travelBlogs } from "../Travelinternational.js";
import CTASection from "../../../../CTASection/CTASection";

import img1 from "./London-skyline.webp";
import img2 from "./A candid shot — couple walking under city lights.webp";
import img3 from "./Boat on Thames with city lights.webp";
import img4 from "./West End street lights or a theatre marquee.webp";
import img5 from "./Camden Town night market or street performance.webp";
import img6 from "./Piccadilly Circus lights.webp";

export default function LondonAfterDark() {
  return (
    <div className={styles.londonBlog}>
      {/* Hero Banner */}
      <div className={styles.heroBanner}>
        <div
          className={styles.heroBg}
          style={{ backgroundImage: `url(${img1})` }}
        ></div>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroText}>
          <h1>London After Dark</h1>
          <p>
            The City That Never Sleeps: Nights filled with lights, music, and
            endless adventures
          </p>
        </div>
      </div>

      <motion.section
        className={styles.introSection}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <p className={styles.intro}>
          Discover the magic of London after sunset — from glittering skylines
          and Thames cruises to vibrant street music, hidden pubs, and timeless
          night charm. Let the city’s energy ignite your wanderlust.
        </p>
      </motion.section>

      {/* Main Content + Sidebar */}
      <div className={styles.blogContainer}>
        <div className={styles.mainContent}>
          <section className={styles.sectionRow}>
            <h2>When the Sun Sets, London Truly Wakes Up</h2>
            <div className={styles.contentRow}>
              <img src={img2} alt="When the Sun Sets, London Truly Wakes Up" />
                  <p >
                As the sun dips below the horizon, London undergoes a magical
                transformation, revealing a side of the city that’s often hidden
                during daylight hours. Streets that bustle with hurried
                commuters and tourists take on a softer, more vibrant rhythm,
                where the glow of vintage lamps and neon signs blend seamlessly
                with the reflections dancing across the Thames. London’s iconic
                landmarks—Big Ben, the London Eye, and Tower Bridge—begin to
                shimmer under the night sky, creating a breathtaking backdrop
                for both residents and travelers seeking inspiration. The city’s
                heartbeat shifts; the chatter of cafés and pubs becomes a
                melodic hum, interspersed with live music spilling from hidden
                corners and alleyways. From intimate jazz bars in Soho to the
                electrifying energy of Covent Garden’s street performers, every
                step through the city streets feels like a carefully
                orchestrated experience, inviting exploration and wonder.
                Culinary delights beckon from food markets and late-night
                eateries, their aromas tempting passersby into immersive
                cultural experiences. The Thames itself becomes a mirror of
                London’s nocturnal spirit, its waters reflecting a tapestry of
                lights and colors that captivate photographers and romantics
                alike. For those wandering along Southbank or peering from a
                rooftop, the city’s skyline tells a story of timeless elegance
                meeting contemporary energy—a narrative that effortlessly pulls
                people in. London after dark is not just about sightseeing; it’s
                about feeling the pulse of a city alive with creativity,
                history, and endless possibilities. Every corner holds a story,
                every street corner a melody, and every glowing bridge a memory
                waiting to be made. Once you step into this nocturnal world,
                it’s nearly impossible to resist its charm or look away,
                compelling you to follow the lights and discover every hidden
                gem that makes London a city that never truly sleeps.
              </p>
            </div>
          </section>

          <section className={`${styles.sectionRow} ${styles.reverse}`}>
            <h2>Thames Nights: Cruising Under the Stars</h2>
            <div className={styles.contentRow}>
              <img src={img3} alt="Thames Cruise Night" />
                <p>
                Gliding along the Thames at night is an experience that
                transforms the ordinary into the extraordinary, allowing
                visitors to witness London’s majestic landmarks in a completely
                new light. As the boat gently cuts through the river, the
                reflections of illuminated bridges, historic buildings, and
                modern skyscrapers dance upon the water, creating a mesmerizing
                canvas that feels almost dreamlike. The iconic Big Ben glows
                with regal brilliance, the London Eye spins gracefully, adorned
                with twinkling lights, and the Shard pierces the sky like a
                beacon of contemporary elegance—all merging seamlessly with
                centuries of architectural history. The river’s gentle lull
                provides a serene escape from the city’s daytime hustle,
                inviting travelers to pause, breathe, and soak in the magic of
                London after dark. Along the banks, street performers, riverside
                cafés, and bustling markets add a human touch to this urban
                fairytale, offering glimpses of the city’s vibrant culture and
                eclectic energy. Each turn of the river reveals new
                perspectives: illuminated walkways, hidden alleyways, and
                charming riverside terraces beckon exploration, capturing the
                hearts of photographers, romantics, and adventure-seekers alike.
                The Thames cruise is not just a visual spectacle; it is a
                multi-sensory experience, where the gentle breeze carries the
                faint aroma of nearby eateries, the soft lapping of water
                complements distant music, and the twinkle of city lights evokes
                a sense of wonder and nostalgia. For couples, families, and solo
                travelers, this nocturnal journey provides a connection to
                London’s soul, highlighting its perfect balance of history,
                modernity, and romance. Once you step aboard, the city’s
                illuminated beauty and quiet intimacy draw you in completely,
                compelling you to linger and savor every moment as the Thames
                transforms the night into an unforgettable symphony of light,
                reflection, and enchantment.
              </p>
            </div>
          </section>

          <section className={styles.sectionRow}>
            <h2>The West End Buzz: Theatre, Music & Glamour</h2>
            <div className={styles.contentRow}>
              <img src={img4} alt="The West End Buzz" />
               <p>
                As night falls over London, the West End comes alive with a
                vibrancy that pulses through every street, alley, and theatre
                marquee. This iconic theatre district is the heart of the city’s
                cultural energy, where stories unfold under sparkling lights,
                and the air buzzes with anticipation and creativity. Walking
                through the West End at night, you are greeted by the glow of
                marquee signs, the chatter of excited audiences, and the
                occasional melody of street performers adding their own flair to
                the evening. From the grandeur of historic theatres like the
                Lyceum and the Palace to smaller, avant-garde venues tucked away
                on bustling streets, the district offers an eclectic mix of
                experiences that captivate both first-time visitors and seasoned
                Londoners. Musicals, plays, and live performances create an
                intoxicating rhythm, blending emotion, spectacle, and artistry
                in a way that is uniquely London. Beyond the theatres, the
                streets themselves are alive: dimly lit cafés, late-night
                eateries, and cocktail bars spill out warm light, inviting
                passersby to pause and soak in the atmosphere. The West End is
                also a sensory feast; the aroma of gourmet cuisine mingles with
                the sound of laughter and applause, while the shimmer of neon
                lights and reflections on wet pavements evoke a cinematic magic.
                For travelers seeking both entertainment and the essence of
                London’s nocturnal charm, this district offers an unforgettable
                immersion into the city’s soul. Every corner tells a story,
                every performance ignites emotion, and every visit leaves an
                indelible impression. The West End at night is not just a
                destination—it’s an experience where music, drama, and glamour
                converge, inviting you to become part of the city’s living
                narrative, linger in its enchantment, and carry its magic with
                you long after the curtains fall.
              </p>
            </div>
          </section>
    <CTASection
  heading="Ready to Turn Your London Dream Into Reality?"
  description="From iconic landmarks to royal charm—London promises timeless memories. Let’s plan your perfect international trip."
  buttonText="Plan Your London Trip"
  buttonLink="tel:+919876543210"
/>
          <section className={`${styles.sectionRow} ${styles.reverse}`}>
            <h2>Street Beats & Food Trails: Camden After Dark</h2>
            <div className={styles.contentRow}>
              <img src={img5} alt="Street Beats & Food Trails: Camden" />
               <p>
                When night falls, Camden Town awakens with a pulse that is
                unmistakably its own—a heady mix of music, flavors, and neon-lit
                streets that draw locals and travelers alike into its vibrant
                embrace. The cobbled lanes and quirky alleyways hum with energy
                as live performers—guitarists, drummers, and vocalists—fill the
                air with spontaneous melodies, creating a soundtrack that makes
                every step feel alive. Street food vendors line the streets,
                offering a feast for the senses: smoky barbecue, aromatic
                curries, gourmet vegan bites, and decadent desserts tempt every
                craving, while the scent of freshly baked bread and sizzling
                skewers drifts through the cool night air. Each corner bursts
                with color and character, from hand-painted murals and
                flickering neon signs to market stalls overflowing with
                handcrafted treasures, vinyl records, and vintage finds. Hidden
                pubs and intimate music venues beckon with the promise of craft
                cocktails and underground beats, inviting visitors to linger and
                soak in Camden’s eclectic spirit. Unlike other nightlife
                hotspots, Camden feels personal, inclusive, and authentic—where
                strangers become friends over shared laughter, music, and a
                mutual love for the city’s creativity. Photographers chase
                vibrant street scenes, capturing moments of energy, motion, and
                light, while wanderers lose themselves in the sensory
                kaleidoscope of sights, sounds, and tastes. Camden after dark is
                not merely a place to visit—it’s an experience to be felt. Every
                rhythm, aroma, and hue tells a story, every alleyway hides a
                surprise, and every night spent here leaves a lasting imprint on
                the memory of those who wander through its spirited streets. For
                anyone craving a night where music, food, and culture converge
                in perfect harmony, Camden delivers an adventure that is as
                unforgettable as London itself.
              </p>
            </div>
          </section>

          <section className={styles.sectionRow}>
            <h2>Illuminated Icons: London’s Night Glow</h2>
            <div className={styles.contentRow}>
              <img src={img6} alt="Illuminated Icons: London" />
               <p>
                As darkness drapes over London, the city transforms into a
                luminous canvas where history, architecture, and modern energy
                converge in breathtaking harmony. Landmarks like Tower Bridge,
                the London Eye, and Piccadilly Circus shimmer with a glow that
                reflects off the Thames, casting a spell over locals and
                visitors alike. Each street corner pulses with life, from the
                quiet elegance of Southbank’s riverside walk to the dynamic
                bustle of neon-lit squares, offering countless photo-worthy
                moments that capture the city’s nocturnal charm. Couples stroll
                hand-in-hand beneath twinkling lights, while street performers
                add rhythm and soul to every pathway, creating an atmosphere
                that is equal parts romance and excitement. The city’s iconic
                buildings, both historic and contemporary, are illuminated to
                reveal intricate details often hidden in daylight, allowing
                observers to appreciate the artistry and grandeur that define
                London’s skyline. Cafés, pubs, and boutique eateries glow
                warmly, inviting passersby to pause and savor both local flavors
                and the ambiance of the night. Photographers chase reflections
                on the Thames, capturing the interplay of water, light, and
                movement in a kaleidoscope of color that tells stories of the
                city’s heritage and modern vibrancy. This nightly spectacle is
                more than just a visual experience—it’s a sensory immersion,
                blending light, sound, and human energy into a rhythm that feels
                uniquely London. From quiet moments of reflection to the vibrant
                pulse of nightlife, illuminated icons create a tapestry that
                connects every visitor to the soul of the city, making London
                not only seen but felt after dark. For those who seek wonder,
                inspiration, or simply a magical evening stroll, the glowing
                landmarks of London promise memories that linger long after the
                night ends.
              </p>
            </div>
          </section>
        </div>

        <aside className={styles.sidebar}>
          <PromoSidebar />
        </aside>
      </div>

      <BlogNavigator
        category="Travelinternational"
        blogs={travelBlogs}
        currentBlogId="london-after-dark"
      />
    </div>
  );
}
